import React, { Suspense, useEffect, useRef, useState, useCallback } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Loader, OrthographicCamera } from '@react-three/drei';
import { Play, RotateCcw, Download, Upload, ChevronLeft, Activity, Box, MousePointer, Paintbrush, Image, Smile, Volume2 } from 'lucide-react';

import * as THREE from 'three';
import axios from 'axios';

import Avatar from '../components/Avatar';
import LipSyncController from '../components/LipSyncController';
import HeadAnimator from '../components/HeadAnimator';
import HoverAnimator from '../components/HoverAnimator';
import SceneBackground from '../components/SceneBackground';
import SceneLighting from '../components/SceneLighting';
import { validateHdrUrl, validateImageUrl, validateVideoUrl } from '../components/SceneBackground';

import {
  loadLipSyncConfig,
  saveLipSyncConfig,
  resetLipSyncConfig
} from '../config/lipSyncDefaults';

import {
  loadSceneConfig,
  saveSceneConfig,
  resetSceneConfig
} from '../config/sceneDefaults';

import {
  loadTtsConfig,
  saveTtsConfig
} from '../config/ttsDefaults';

const __connectedAudioElements = new WeakMap();

function MixerUpdater({ mixer }) {
  useFrame((state, delta) => {
    if (mixer) mixer.update(delta);
  });
  return null;
}

// Audio frequency band visualizer
function AudioBandVisualizer({ audioRef, playing }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) return;
    const audioEl = audioRef.current;

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioContextRef.current;

    if (!analyserRef.current) {
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    }

    if (!__connectedAudioElements.has(audioEl)) {
      try {
        const source = audioCtx.createMediaElementSource(audioEl);
        source.connect(analyserRef.current);
        analyserRef.current.connect(audioCtx.destination);
        __connectedAudioElements.set(audioEl, true);
      } catch (e) {}
    }
  }, [audioRef]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyserRef.current || !dataArrayRef.current) {
      animRef.current = requestAnimationFrame(draw);
      return;
    }
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    analyser.getByteFrequencyData(dataArray);

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(0, 0, w, h);

    // Band definitions
    const bands = [
      { start: 2, end: 14, color: '#ef4444', label: 'Graves' },
      { start: 14, end: 44, color: '#f59e0b', label: 'Medias' },
      { start: 44, end: 96, color: '#10b981', label: 'Agudas' },
    ];

    const barW = (w - 40) / bands.length;

    bands.forEach((band, i) => {
      let sum = 0;
      let count = 0;
      for (let j = band.start; j < band.end && j < dataArray.length; j++) {
        sum += dataArray[j];
        count++;
      }
      const avg = count > 0 ? sum / count : 0;
      const barH = (avg / 255) * (h - 30);

      const x = 20 + i * barW + barW * 0.2;
      const bw = barW * 0.6;

      // Bar
      ctx.fillStyle = band.color;
      ctx.fillRect(x, h - barH - 5, bw, barH);

      // Label
      ctx.fillStyle = '#fff';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(band.label, x + bw / 2, h - 2);

      // Value
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.font = '10px sans-serif';
      ctx.fillText(Math.round(avg), x + bw / 2, h - barH - 10);
    });

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={120}
      style={{ width: '100%', height: '100px', borderRadius: '8px', marginTop: '8px' }}
    />
  );
}

// Single slider component
function ConfigSlider({ label, value, min, max, step, onChange, color = '#3b82f6' }) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#d1d5db', marginBottom: '2px' }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace', color: '#fff' }}>{Number(value).toFixed(step < 0.1 ? 3 : 2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          width: '100%',
          accentColor: color,
          height: '4px',
          cursor: 'pointer'
        }}
      />
    </div>
  );
}

// Toggle switch component
function ConfigToggle({ label, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <span style={{ fontSize: '13px', color: '#d1d5db' }}>{label}</span>
      <button
        onClick={() => onChange(!checked)}
        style={{
          width: '40px',
          height: '22px',
          borderRadius: '11px',
          border: 'none',
          background: checked ? '#3b82f6' : 'rgba(255,255,255,0.15)',
          cursor: 'pointer',
          position: 'relative',
          transition: 'background 0.2s'
        }}
      >
        <div style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: '2px',
          left: checked ? '20px' : '2px',
          transition: 'left 0.2s'
        }} />
      </button>
    </div>
  );
}

// Color picker component
function ConfigColor({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <span style={{ fontSize: '13px', color: '#d1d5db' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '32px', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }}
        />
        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#fff' }}>{value}</span>
      </div>
    </div>
  );
}

// Morph target editor section
function MorphTargetEditor({ config, onChange }) {
  const [search, setSearch] = useState('');
  const targets = Object.entries(config.morphTargets || {})
    .filter(([key]) => key.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <input
        type="text"
        placeholder="Buscar morph target..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.3)',
          color: '#fff',
          fontSize: '13px',
          marginBottom: '12px'
        }}
      />
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
        {targets.map(([key, mapping]) => (
          <div key={key} style={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{key}</div>
            <ConfigSlider label="Low Freq" value={mapping.low || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'low', v)} color="#ef4444" />
            <ConfigSlider label="Mid Freq" value={mapping.mid || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'mid', v)} color="#f59e0b" />
            <ConfigSlider label="High Freq" value={mapping.high || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'high', v)} color="#10b981" />
            <ConfigSlider label="Volume RMS" value={mapping.volume || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'volume', v)} color="#14b8a6" />
            <ConfigSlider label="Text Cue" value={mapping.text || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'text', v)} color="#f97316" />
            <ConfigSlider label="Max Value" value={mapping.max || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('morphTargets', key, 'max', v)} color="#8b5cf6" />
            <ConfigSlider label="Lerp Speed" value={mapping.lerp || 0.1} min={0.01} max={0.5} step={0.01} onChange={(v) => onChange('morphTargets', key, 'lerp', v)} color="#06b6d4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Bone editor section
function BoneEditor({ config, onChange }) {
  const [search, setSearch] = useState('');
  const bones = Object.entries(config.bones || {})
    .filter(([key]) => key.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <input
        type="text"
        placeholder="Buscar bone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.3)',
          color: '#fff',
          fontSize: '13px',
          marginBottom: '12px'
        }}
      />
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
        {bones.map(([key, mapping]) => (
          <div key={key} style={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{key}</div>
            <ConfigSlider label="Low Freq" value={mapping.low || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('bones', key, 'low', v)} color="#ef4444" />
            <ConfigSlider label="Mid Freq" value={mapping.mid || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('bones', key, 'mid', v)} color="#f59e0b" />
            <ConfigSlider label="High Freq" value={mapping.high || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('bones', key, 'high', v)} color="#10b981" />
            <ConfigSlider label="Volume RMS" value={mapping.volume || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('bones', key, 'volume', v)} color="#14b8a6" />
            <ConfigSlider label="Text Cue" value={mapping.text || 0} min={0} max={1} step={0.01} onChange={(v) => onChange('bones', key, 'text', v)} color="#f97316" />
            <ConfigSlider label="Max Rot X" value={mapping.maxRotX || 0} min={-0.5} max={0.5} step={0.001} onChange={(v) => onChange('bones', key, 'maxRotX', v)} color="#8b5cf6" />
            <ConfigSlider label="Max Rot Y" value={mapping.maxRotY || 0} min={-0.5} max={0.5} step={0.001} onChange={(v) => onChange('bones', key, 'maxRotY', v)} color="#a855f7" />
            <ConfigSlider label="Max Rot Z" value={mapping.maxRotZ || 0} min={-0.5} max={0.5} step={0.001} onChange={(v) => onChange('bones', key, 'maxRotZ', v)} color="#c084fc" />
            <ConfigSlider label="Max Pos X" value={mapping.maxPosX || 0} min={-0.1} max={0.1} step={0.001} onChange={(v) => onChange('bones', key, 'maxPosX', v)} color="#f472b6" />
            <ConfigSlider label="Max Pos Y" value={mapping.maxPosY || 0} min={-0.1} max={0.1} step={0.001} onChange={(v) => onChange('bones', key, 'maxPosY', v)} color="#ec4899" />
            <ConfigSlider label="Max Pos Z" value={mapping.maxPosZ || 0} min={-0.1} max={0.1} step={0.001} onChange={(v) => onChange('bones', key, 'maxPosZ', v)} color="#db2777" />
            <ConfigSlider label="Lerp Speed" value={mapping.lerp || 0.1} min={0.01} max={0.5} step={0.01} onChange={(v) => onChange('bones', key, 'lerp', v)} color="#06b6d4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Global settings editor
function GlobalEditor({ config, onChange }) {
  return (
    <div>
      <ConfigSlider
        label="Smoothing Global (Lerp)"
        value={config.global?.smoothing || 0.12}
        min={0.01}
        max={0.5}
        step={0.01}
        onChange={(v) => onChange('global', null, 'smoothing', v)}
        color="#06b6d4"
      />
      <ConfigSlider
        label="Attack"
        value={config.global?.attack || 0.24}
        min={0.01}
        max={0.6}
        step={0.01}
        onChange={(v) => onChange('global', null, 'attack', v)}
        color="#22c55e"
      />
      <ConfigSlider
        label="Release"
        value={config.global?.release || 0.16}
        min={0.01}
        max={0.6}
        step={0.01}
        onChange={(v) => onChange('global', null, 'release', v)}
        color="#f97316"
      />
      <ConfigSlider
        label="Power Curve"
        value={config.global?.powerCurve || 1.5}
        min={0.5}
        max={3}
        step={0.1}
        onChange={(v) => onChange('global', null, 'powerCurve', v)}
        color="#f59e0b"
      />
      <ConfigSlider
        label="Normalize Divisor"
        value={config.global?.normalizeDivisor || 128}
        min={64}
        max={256}
        step={1}
        onChange={(v) => onChange('global', null, 'normalizeDivisor', v)}
        color="#10b981"
      />
      <ConfigSlider
        label="Noise Floor"
        value={config.global?.noiseFloor ?? 4}
        min={0}
        max={40}
        step={1}
        onChange={(v) => onChange('global', null, 'noiseFloor', v)}
        color="#64748b"
      />
      <ConfigSlider
        label="Text Influence"
        value={config.global?.textInfluence ?? 0.18}
        min={0}
        max={1}
        step={0.01}
        onChange={(v) => onChange('global', null, 'textInfluence', v)}
        color="#f97316"
      />
      <div style={{ marginTop: '16px', fontSize: '12px', color: '#9ca3af' }}>
        <p><strong>Low Freq bins:</strong> {config.bands?.low?.startBin} – {config.bands?.low?.endBin}</p>
        <p><strong>Mid Freq bins:</strong> {config.bands?.mid?.startBin} – {config.bands?.mid?.endBin}</p>
        <p><strong>High Freq bins:</strong> {config.bands?.high?.startBin} – {config.bands?.high?.endBin}</p>
      </div>
    </div>
  );
}

// Model editor
function ModelEditor({ sceneConfig, onSceneChange }) {
  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Modelo 3D</div>
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>URL do modelo GLB</label>
        <input
          type="text"
          value={sceneConfig.avatar?.url || ''}
          onChange={(e) => onSceneChange('avatar', 'url', e.target.value)}
          placeholder="/model.glb"
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: '13px'
          }}
        />
      </div>
      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
        Use o caminho relativo (ex: /model.glb) ou URL completa. Recarregue a página para aplicar mudanças de modelo.
      </div>
    </div>
  );
}

// Hover editor
function HoverEditor({ sceneConfig, onSceneChange }) {
  const hover = sceneConfig.hover || {};
  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Animação de Hover</div>
      <ConfigToggle
        label="Ativar hover"
        checked={hover.enabled !== false}
        onChange={(v) => onSceneChange('hover', 'enabled', v)}
      />
      <ConfigSlider label="Escala no hover" value={hover.scale ?? 1.02} min={1} max={1.15} step={0.001} onChange={(v) => onSceneChange('hover', 'scale', v)} color="#8b5cf6" />
      <ConfigSlider label="Elevação Y" value={hover.liftY ?? 0.02} min={0} max={0.15} step={0.001} onChange={(v) => onSceneChange('hover', 'liftY', v)} color="#ec4899" />
      <ConfigSlider label="Velocidade de transição" value={hover.transitionSpeed ?? 0.1} min={0.01} max={0.5} step={0.01} onChange={(v) => onSceneChange('hover', 'transitionSpeed', v)} color="#06b6d4" />
    </div>
  );
}

// Material editor
function MaterialEditor({ sceneConfig, onSceneChange }) {
  const mats = sceneConfig.materials || {};
  const matNames = [
    { key: 'body', label: 'Corpo' },
    { key: 'eyes', label: 'Olhos' },
    { key: 'teeth', label: 'Dentes' },
    { key: 'hair', label: 'Cabelo' },
    { key: 'tshirt', label: 'Camiseta' },
  ];

  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Materiais</div>
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
        {matNames.map(({ key, label }) => {
          const mat = mats[key] || {};
          return (
            <div key={key} style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '10px'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{label}</div>
              <ConfigSlider label="Roughness" value={mat.roughness ?? 0.5} min={0} max={2} step={0.01} onChange={(v) => onSceneChange('materials', key, { ...mat, roughness: v })} color="#f59e0b" />
              <ConfigSlider label="Metalness" value={mat.metalness ?? 0} min={0} max={1} step={0.01} onChange={(v) => onSceneChange('materials', key, { ...mat, metalness: v })} color="#94a3b8" />
              <ConfigColor label="Cor" value={mat.color || '#ffffff'} onChange={(v) => onSceneChange('materials', key, { ...mat, color: v })} />
              <ConfigSlider label="Env Map Intensity" value={mat.envMapIntensity ?? 0.5} min={0} max={3} step={0.05} onChange={(v) => onSceneChange('materials', key, { ...mat, envMapIntensity: v })} color="#10b981" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Background editor
function LightEditor({ label, lightKey, lightConfig, onSceneChange }) {
  const lc = lightConfig || {};
  const pos = lc.position || { x: 0, y: 0, z: 0 };
  const isDirectional = lightKey === 'directional';
  const isPoint = lightKey === 'point';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '8px',
      padding: '10px 12px',
      marginBottom: '10px'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{label}</span>
        <ConfigToggle
          label=""
          checked={lc.enabled !== false}
          onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, enabled: v })}
        />
      </div>
      {lc.enabled !== false && (
        <>
          <ConfigColor label="Cor" value={lc.color || '#ffffff'} onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, color: v })} />
          <ConfigSlider label="Intensidade" value={lc.intensity ?? (isDirectional ? 1.2 : 0.6)} min={0} max={3} step={0.05} onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, intensity: v })} color="#f59e0b" />
          {(isDirectional || isPoint) && (
            <>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', marginTop: '4px' }}>Posição</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <ConfigSlider label="X" value={pos.x ?? (isDirectional ? 5 : -3)} min={-20} max={20} step={0.1} onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, position: { ...pos, x: v } })} color="#ef4444" />
                </div>
                <div style={{ flex: 1 }}>
                  <ConfigSlider label="Y" value={pos.y ?? (isDirectional ? 10 : 4)} min={-20} max={20} step={0.1} onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, position: { ...pos, y: v } })} color="#10b981" />
                </div>
                <div style={{ flex: 1 }}>
                  <ConfigSlider label="Z" value={pos.z ?? (isDirectional ? 7 : 3)} min={-20} max={20} step={0.1} onChange={(v) => onSceneChange('lighting', lightKey, { ...lc, position: { ...pos, z: v } })} color="#3b82f6" />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ValidatedUrlInput({ label, value, placeholder, onChange, validator, errorMsg, defaultValue }) {
  const isValid = !value || validator(value);
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={{ fontSize: '12px', color: '#9ca3af' }}>{label}</label>
        {defaultValue && (
          <button
            onClick={() => onChange(defaultValue)}
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.06)',
              color: '#94a3b8',
              fontSize: '11px',
              cursor: 'pointer'
            }}
          >
            Restaurar padrão
          </button>
        )}
      </div>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: '6px',
          border: isValid ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(239,68,68,0.6)',
          background: 'rgba(0,0,0,0.3)',
          color: '#fff',
          fontSize: '13px',
          outline: 'none'
        }}
      />
      {!isValid && (
        <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '4px' }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
}

function BackgroundEditor({ sceneConfig, onSceneChange }) {
  const bg = sceneConfig.background || {};
  const types = [
    { key: 'hdr', label: 'HDR Environment' },
    { key: 'image', label: 'Imagem' },
    { key: 'video', label: 'Vídeo' },
    { key: 'color', label: 'Cor Sólida' },
  ];

  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Fundo da Cena</div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {types.map(t => (
          <button
            key={t.key}
            onClick={() => onSceneChange('background', 'type', t.key)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: bg.type === t.key ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)',
              color: bg.type === t.key ? '#fff' : '#94a3b8',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {bg.type === 'hdr' && (
        <>
          <ValidatedUrlInput
            label="URL do HDR"
            value={bg.hdrUrl || ''}
            placeholder="/images/studio_small_08_4k.hdr"
            onChange={(v) => onSceneChange('background', 'hdrUrl', v)}
            validator={validateHdrUrl}
            errorMsg="Extensão inválida. Use .hdr ou .exr"
            defaultValue="/images/studio_small_08_4k.hdr"
          />
          <ConfigSlider label="Rotação HDR (graus)" value={bg.hdrRotation ?? 0} min={0} max={360} step={1} onChange={(v) => onSceneChange('background', 'hdrRotation', v)} color="#8b5cf6" />
          <ConfigSlider label="Intensidade HDR (overlay escuro)" value={bg.hdrIntensity ?? 1} min={0} max={2} step={0.01} onChange={(v) => onSceneChange('background', 'hdrIntensity', v)} color="#f59e0b" />
        </>
      )}

      {bg.type === 'image' && (
        <ValidatedUrlInput
          label="URL da imagem"
          value={bg.imageUrl || ''}
          placeholder="/images/background.jpg"
          onChange={(v) => onSceneChange('background', 'imageUrl', v)}
          validator={validateImageUrl}
          errorMsg="Extensão inválida. Use .jpg, .png, .webp, .gif, .bmp ou .tiff"
          defaultValue=""
        />
      )}

      {bg.type === 'video' && (
        <ValidatedUrlInput
          label="URL do vídeo"
          value={bg.videoUrl || ''}
          placeholder="/videos/background.mp4"
          onChange={(v) => onSceneChange('background', 'videoUrl', v)}
          validator={validateVideoUrl}
          errorMsg="Extensão inválida. Use .mp4, .webm, .ogg ou .mov"
          defaultValue=""
        />
      )}

      {bg.type === 'color' && (
        <ConfigColor label="Cor de fundo" value={bg.color || '#0f172a'} onChange={(v) => onSceneChange('background', 'color', v)} />
      )}

      {/* Lighting section */}
      <div style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Iluminação</div>
        <LightEditor label="Ambient Light" lightKey="ambient" lightConfig={sceneConfig.lighting?.ambient} onSceneChange={onSceneChange} />
        <LightEditor label="Sun Light (Direcional)" lightKey="directional" lightConfig={sceneConfig.lighting?.directional} onSceneChange={onSceneChange} />
        <LightEditor label="Point Light" lightKey="point" lightConfig={sceneConfig.lighting?.point} onSceneChange={onSceneChange} />
      </div>
    </div>
  );
}

function ExpressionEditor({ sceneConfig, onSceneChange }) {
  const expr = sceneConfig.expression || {};
  const anim = sceneConfig.animation || {};

  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Expressão Facial</div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' }}>
        <ConfigSlider label="Sorriso" value={expr.smile ?? 0} min={0} max={1} step={0.01} onChange={(v) => onSceneChange('expression', 'smile', v)} color="#ec4899" />
      </div>

      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Animação Neutra (Idle)</div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', marginBottom: '8px' }}>Cabeça</div>
        <ConfigSlider label="Amplitude" value={anim.headAmplitude ?? 1} min={0} max={2} step={0.05} onChange={(v) => onSceneChange('animation', 'headAmplitude', v)} color="#8b5cf6" />
        <ConfigSlider label="Velocidade" value={anim.headSpeed ?? 1} min={0.1} max={3} step={0.05} onChange={(v) => onSceneChange('animation', 'headSpeed', v)} color="#06b6d4" />
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', marginBottom: '8px' }}>Pescoço</div>
        <ConfigSlider label="Amplitude" value={anim.neckAmplitude ?? 1} min={0} max={2} step={0.05} onChange={(v) => onSceneChange('animation', 'neckAmplitude', v)} color="#8b5cf6" />
        <ConfigSlider label="Velocidade" value={anim.neckSpeed ?? 1} min={0.1} max={3} step={0.05} onChange={(v) => onSceneChange('animation', 'neckSpeed', v)} color="#06b6d4" />
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', marginBottom: '8px' }}>Falando</div>
        <ConfigSlider label="Retorno ao centro (speed)" value={anim.returnSpeed ?? 1} min={0.5} max={5} step={0.1} onChange={(v) => onSceneChange('animation', 'returnSpeed', v)} color="#10b981" />
        <ConfigSlider label="Fator subtíl (movimento em fala)" value={anim.subtleFactor ?? 0.2} min={0} max={1} step={0.05} onChange={(v) => onSceneChange('animation', 'subtleFactor', v)} color="#f59e0b" />
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af', marginBottom: '8px' }}>Piscar de olhos</div>
        <ConfigSlider label="Intervalo mínimo (s)" value={anim.blinkIntervalMin ?? 2} min={0.5} max={5} step={0.1} onChange={(v) => onSceneChange('animation', 'blinkIntervalMin', v)} color="#ef4444" />
        <ConfigSlider label="Intervalo máximo (s)" value={anim.blinkIntervalMax ?? 4} min={1} max={10} step={0.1} onChange={(v) => onSceneChange('animation', 'blinkIntervalMax', v)} color="#ef4444" />
        <ConfigSlider label="Velocidade da piscada" value={anim.blinkSpeed ?? 15} min={5} max={30} step={1} onChange={(v) => onSceneChange('animation', 'blinkSpeed', v)} color="#3b82f6" />
      </div>
    </div>
  );
}

function TtsEditor({ ttsConfig, onChange }) {
  const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
  const models = ['tts-1', 'tts-1-hd'];
  const formats = ['mp3', 'opus', 'aac', 'flac', 'wav'];

  const selectStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.3)',
    color: '#fff',
    fontSize: '13px',
    marginBottom: '10px',
    cursor: 'pointer'
  };

  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Configuração TTS (OpenAI)</div>

      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>API Key da OpenAI (opcional — usa a do backend se vazio)</label>
        <input
          type="password"
          value={ttsConfig.apiKey || ''}
          onChange={(e) => onChange('apiKey', e.target.value)}
          placeholder="sk-..."
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: '13px',
            marginBottom: '10px',
            outline: 'none'
          }}
        />

        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>Voz</label>
        <select style={selectStyle} value={ttsConfig.voice || 'onyx'} onChange={(e) => onChange('voice', e.target.value)}>
          {voices.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>Modelo</label>
        <select style={selectStyle} value={ttsConfig.model || 'tts-1'} onChange={(e) => onChange('model', e.target.value)}>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>Formato</label>
        <select style={selectStyle} value={ttsConfig.response_format || 'mp3'} onChange={(e) => onChange('response_format', e.target.value)}>
          {formats.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <ConfigSlider label="Velocidade (speed)" value={ttsConfig.speed ?? 1.0} min={0.25} max={4.0} step={0.05} onChange={(v) => onChange('speed', v)} color="#10b981" />
      </div>

      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px', marginTop: '20px' }}>Streaming e Chunks</div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>Modelo Chat</label>
        <input
          type="text"
          value={ttsConfig.chatModel || 'gpt-4o-mini'}
          onChange={(e) => onChange('chatModel', e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: '13px',
            marginBottom: '10px',
            outline: 'none'
          }}
        />
        <ConfigSlider label="Max tokens chat" value={ttsConfig.chatMaxTokens ?? 600} min={80} max={2000} step={10} onChange={(v) => onChange('chatMaxTokens', v)} color="#3b82f6" />
        <ConfigSlider label="Temperatura" value={ttsConfig.temperature ?? 0.7} min={0} max={1.5} step={0.05} onChange={(v) => onChange('temperature', v)} color="#f59e0b" />
        <ConfigSlider label="Min chars por TTS" value={ttsConfig.ttsChunkMinChars ?? 35} min={10} max={160} step={5} onChange={(v) => onChange('ttsChunkMinChars', v)} color="#14b8a6" />
        <ConfigSlider label="Max chars por TTS" value={ttsConfig.ttsChunkMaxChars ?? 240} min={80} max={800} step={10} onChange={(v) => onChange('ttsChunkMaxChars', v)} color="#06b6d4" />
      </div>

      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '12px', marginTop: '20px' }}>Prompt de Sistema (Chat)</div>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
        <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '6px' }}>System Prompt</label>
        <textarea
          value={ttsConfig.systemPrompt || ''}
          onChange={(e) => onChange('systemPrompt', e.target.value)}
          rows={6}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      </div>

      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
        Pitch não é suportado pela API OpenAI TTS.
      </div>
    </div>
  );
}

function AdminPage() {
  const [config, setConfig] = useState(() => loadLipSyncConfig());
  const [sceneConfig, setSceneConfig] = useState(() => loadSceneConfig());
  const [ttsConfig, setTtsConfig] = useState(() => loadTtsConfig());
  const [activeTab, setActiveTab] = useState('morphs');
  const [testText, setTestText] = useState('Olá, tudo bem? Eu estou testando o lip sync.');
  const [audioSource, setAudioSource] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [avatarRef, setAvatarRef] = useState(null);
  const [mixer, setMixer] = useState(null);
  const [, setMorphDict] = useState(null);
  const [previewValue, setPreviewValue] = useState(null);
  const audioPlayer = useRef();

  // Save lip sync config whenever it changes
  useEffect(() => {
    saveLipSyncConfig(config);
  }, [config]);

  // Save scene config whenever it changes
  useEffect(() => {
    saveSceneConfig(sceneConfig);
  }, [sceneConfig]);

  // Save TTS config whenever it changes
  useEffect(() => {
    saveTtsConfig(ttsConfig);
  }, [ttsConfig]);

  useEffect(() => {
    if (avatarRef && !mixer) {
      setMixer(new THREE.AnimationMixer(avatarRef));
    }
  }, [avatarRef, mixer]);

  const handleConfigChange = useCallback((section, key, property, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (section === 'global') {
        next.global[property] = value;
      } else {
        next[section][key][property] = value;
      }
      return next;
    });
  }, []);

  const handleSceneChange = useCallback((section, key, value) => {
    setSceneConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (section === 'materials') {
        next.materials[key] = value;
      } else if (section === 'background') {
        next.background[key] = value;
      } else if (section === 'hover') {
        next.hover[key] = value;
      } else if (section === 'avatar') {
        next.avatar[key] = value;
      } else if (section === 'lighting') {
        next.lighting[key] = value;
      } else if (section === 'expression') {
        next.expression[key] = value;
      } else if (section === 'animation') {
        next.animation[key] = value;
      }
      return next;
    });
  }, []);

  const handleTtsChange = useCallback((key, value) => {
    setTtsConfig(prev => {
      const next = { ...prev };
      next[key] = value;
      return next;
    });
  }, []);

  const handleTestAudio = async () => {
    if (!testText.trim()) return;
    try {
      setPlaying(false);
      if (audioPlayer.current) {
        audioPlayer.current.pause();
        audioPlayer.current.src = '';
      }
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(
        `${apiUrl}/api/tts`,
        {
          model: ttsConfig.model || 'tts-1',
          voice: ttsConfig.voice || 'onyx',
          input: testText,
          response_format: ttsConfig.response_format || 'mp3',
          speed: ttsConfig.speed ?? 1.0,
        },
        {
          responseType: 'blob'
        }
      );
      const audioUrl = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      setAudioSource(audioUrl);
      if (audioPlayer.current) {
        audioPlayer.current.src = audioUrl;
        requestAnimationFrame(() => {
          audioPlayer.current?.play().then(() => {
            setPlaying(true);
          }).catch(err => {
            console.warn('Audio play failed (browser may require manual play):', err);
          });
        });
      }
    } catch (e) {
      alert('Erro ao gerar áudio: ' + e.message);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lipsync-config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        setConfig(imported);
        saveLipSyncConfig(imported);
        alert('Configuração importada com sucesso!');
      } catch (err) {
        alert('Erro ao importar JSON: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleReset = () => {
    if (window.confirm('Tem certeza que deseja resetar para os valores padrão?')) {
      const defaults = resetLipSyncConfig();
      setConfig(defaults);
    }
  };

  const handleSceneReset = () => {
    if (window.confirm('Tem certeza que deseja resetar as configurações de cena para os valores padrão?')) {
      const defaults = resetSceneConfig();
      setSceneConfig(defaults);
    }
  };

  const handlePlayerEnded = () => {
    setPlaying(false);
    setAudioSource(null);
  };

  const tabs = [
    { key: 'morphs', label: 'Morph Targets', icon: Activity },
    { key: 'bones', label: 'Bones Faciais', icon: Activity },
    { key: 'global', label: 'Global', icon: Activity },
    { key: 'modelo', label: 'Modelo', icon: Box },
    { key: 'hover', label: 'Hover', icon: MousePointer },
    { key: 'materiais', label: 'Materiais', icon: Paintbrush },
    { key: 'fundo', label: 'Fundo', icon: Image },
    { key: 'expression', label: 'Expressão', icon: Smile },
    { key: 'tts', label: 'TTS', icon: Volume2 },
  ];

  const isSceneTab = ['modelo', 'hover', 'materiais', 'fundo', 'expression'].includes(activeTab);

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif' }}>
      {/* Left: 3D Preview + Audio Test */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Header */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          zIndex: 10,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a href="/" style={{ color: '#94a3b8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <ChevronLeft size={16} />
              Voltar
            </a>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#fff' }}>
              <Activity size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Lip-Sync Admin
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleExport} style={btnStyle} title="Exportar JSON">
              <Download size={14} /> Exportar
            </button>
            <label style={btnStyle}>
              <Upload size={14} /> Importar
              <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
            </label>
            <button onClick={handleReset} style={{ ...btnStyle, background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}>
              <RotateCcw size={14} /> Reset
            </button>
          </div>
        </div>

        {/* 3D Canvas */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Canvas dpr={2} onCreated={(ctx) => { ctx.gl.physicallyCorrectLights = true; }}>
            <OrthographicCamera makeDefault zoom={2000} position={[0, 1.65, 1]} />
            <Suspense fallback={null}>
              <SceneBackground config={sceneConfig.background} />
            </Suspense>
            <SceneLighting config={sceneConfig.lighting} />
            <Suspense fallback={null}>
              <HoverAnimator
                enabled={sceneConfig.hover?.enabled !== false}
                scale={sceneConfig.hover?.scale ?? 1.02}
                liftY={sceneConfig.hover?.liftY ?? 0.02}
                transitionSpeed={sceneConfig.hover?.transitionSpeed ?? 0.1}
              >
                <Avatar
                  avatar_url={sceneConfig.avatar?.url || '/model.glb'}
                  setMorphTargetDictionaryBody={setMorphDict}
                  setAvatarRef={setAvatarRef}
                  mixer={mixer}
                  materialConfig={sceneConfig.materials}
                />
              </HoverAnimator>
            </Suspense>
            <MixerUpdater mixer={mixer} />
            <LipSyncController audioPlayerRef={audioPlayer} avatarRef={avatarRef} playing={playing} previewValue={previewValue} speechText={testText} />
            <HeadAnimator avatarRef={avatarRef} playing={playing} animationConfig={sceneConfig.animation} />
          </Canvas>
          <Loader dataInterpolation={(p) => `Loading...`} />
        </div>

        {/* Preview intensity slider */}
        <div style={{
          padding: '12px 16px 0',
          background: 'rgba(15,23,42,0.95)',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', color: '#94a3b8', whiteSpace: 'nowrap' }}>👁 Preview:</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={previewValue ?? 0}
            onChange={(e) => setPreviewValue(parseFloat(e.target.value) || null)}
            style={{ flex: 1, minWidth: '120px', accentColor: '#8b5cf6', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '13px', color: '#fff', fontFamily: 'monospace', minWidth: '40px' }}>
            {previewValue?.toFixed(2) ?? '0.00'}
          </span>
          <button
            onClick={() => setPreviewValue(null)}
            style={{
              padding: '4px 10px',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.06)',
              color: '#cbd5e1',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Off
          </button>
        </div>

        {/* Audio test panel */}
        <div style={{
          padding: '16px',
          background: 'rgba(15,23,42,0.95)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <input
            type="text"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(0,0,0,0.3)',
              color: '#fff',
              fontSize: '14px'
            }}
          />
          <button
            onClick={handleTestAudio}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: '#3b82f6',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Play size={16} /> Gerar Áudio
          </button>
          {audioSource && (
            <button
              onClick={() => {
                if (audioPlayer.current) {
                  audioPlayer.current.play().then(() => setPlaying(true)).catch(err => {
                    console.warn('Play failed:', err);
                  });
                }
              }}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                background: '#10b981',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Play size={16} /> Tocar
            </button>
          )}

          <audio
            ref={audioPlayer}
            src={audioSource || undefined}
            onEnded={handlePlayerEnded}
            controls
            style={{ width: '100%', height: '36px', borderRadius: '8px' }}
          />
        </div>

        {/* Audio visualizer */}
        <div style={{ padding: '0 16px 12px', background: 'rgba(15,23,42,0.95)' }}>
          <AudioBandVisualizer audioRef={audioPlayer} playing={playing} />
        </div>
      </div>

      {/* Right: Config Panel */}
      <div style={{
        width: '420px',
        minWidth: '320px',
        background: '#1e293b',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          overflowX: 'auto'
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: 1,
                  padding: '12px 6px',
                  background: activeTab === tab.key ? 'rgba(59,130,246,0.15)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.key ? '#fff' : '#94a3b8',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'hidden', padding: '16px' }}>
          {activeTab === 'morphs' && <MorphTargetEditor config={config} onChange={handleConfigChange} />}
          {activeTab === 'bones' && <BoneEditor config={config} onChange={handleConfigChange} />}
          {activeTab === 'global' && <GlobalEditor config={config} onChange={handleConfigChange} />}
          {activeTab === 'modelo' && <ModelEditor sceneConfig={sceneConfig} onSceneChange={handleSceneChange} />}
          {activeTab === 'hover' && <HoverEditor sceneConfig={sceneConfig} onSceneChange={handleSceneChange} />}
          {activeTab === 'materiais' && <MaterialEditor sceneConfig={sceneConfig} onSceneChange={handleSceneChange} />}
          {activeTab === 'fundo' && <BackgroundEditor sceneConfig={sceneConfig} onSceneChange={handleSceneChange} />}
          {activeTab === 'expression' && <ExpressionEditor sceneConfig={sceneConfig} onSceneChange={handleSceneChange} />}
          {activeTab === 'tts' && <TtsEditor ttsConfig={ttsConfig} onChange={handleTtsChange} />}
        </div>

        {/* Status footer */}
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontSize: '11px',
          color: '#64748b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {isSceneTab ? (
            <button
              onClick={handleSceneReset}
              style={{
                padding: '4px 10px',
                borderRadius: '6px',
                border: '1px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.1)',
                color: '#fca5a5',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Reset Cena
            </button>
          ) : (
            <span>Morphs: {Object.keys(config.morphTargets || {}).length}</span>
          )}
          <span>Bones: {Object.keys(config.bones || {}).length}</span>
        </div>
      </div>
    </div>
  );
}

const btnStyle = {
  padding: '6px 12px',
  borderRadius: '6px',
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.06)',
  color: '#cbd5e1',
  fontSize: '12px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'background 0.2s'
};

export default AdminPage;
